'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

type Tab = 'login' | 'signup';
type Role = 'reviewer' | 'brand';
type SignupStep = 'credentials' | 'profile' | 'verify-email';

interface ReviewerProfile {
  bio: string;
  city: string;
  categories: string[];
  instagram: string;
  youtube: string;
}

interface BrandProfile {
  company_name: string;
  website: string;
  industry: string;
  gst_number: string;
}

const REVIEWER_CATEGORIES = [
  'Electronics', 'Fashion', 'Beauty', 'Food & Beverage', 'Home & Kitchen',
  'Sports', 'Books', 'Toys', 'Health', 'Travel',
];

const INDUSTRIES = [
  'E-commerce', 'FMCG', 'Technology', 'Fashion & Apparel', 'Food & Beverage',
  'Health & Wellness', 'Home Decor', 'Automotive', 'Education', 'Other',
];

export default function AuthPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();

  const [tab, setTab] = useState<Tab>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signupStep, setSignupStep] = useState<SignupStep>('credentials');
  const [selectedRole, setSelectedRole] = useState<Role>('reviewer');
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  // Login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Signup credential fields
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupMobile, setSignupMobile] = useState('');
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  // Reviewer profile fields
  const [reviewerProfile, setReviewerProfile] = useState<ReviewerProfile>({
    bio: '', city: '', categories: [], instagram: '', youtube: '',
  });

  // Brand profile fields
  const [brandProfile, setBrandProfile] = useState<BrandProfile>({
    company_name: '', website: '', industry: '', gst_number: '',
  });

  // Stored user id after signup (for profile upsert)
  const [signedUpUserId, setSignedUpUserId] = useState<string | null>(null);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Redirect already-logged-in users
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) return;
      const redirectParam = searchParams.get('redirect');
      if (redirectParam) { router.replace(redirectParam); return; }
      const { data: profile } = await supabase
        .from('user_profiles').select('role').eq('id', session.user.id).maybeSingle();
      if (profile?.role === 'seller') router.replace('/seller/dashboard');
      else if (profile?.role === 'admin') router.replace('/admin/applications');
      else router.replace('/reviewer/dashboard');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: loginEmail, password: loginPassword,
      });
      if (signInError) throw signInError;

      const redirectParam = searchParams.get('redirect');
      if (redirectParam) { router.push(redirectParam); return; }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles').select('role').eq('id', user.id).maybeSingle();
        if (profile?.role === 'seller') {
          // Check if seller has completed the 4-step setup flow
          const { data: sellerProfile } = await supabase
            .from('seller_profiles')
            .select('setup_completed')
            .eq('user_id', user.id)
            .maybeSingle();
          if (!sellerProfile?.setup_completed) {
            router.push('/seller/setup');
          } else {
            router.push('/seller/dashboard');
          }
        } else if (profile?.role === 'admin') router.push('/admin/applications');
        else router.push('/reviewer/dashboard');
      } else {
        router.push('/reviewer/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (signupPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://indianrevi4027.builtwithrocket.new';
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          data: { full_name: signupName, role: selectedRole === 'brand' ? 'seller' : 'reviewer' },
          emailRedirectTo: `${siteUrl}/auth/callback`,
        },
      });
      if (signUpError) throw signUpError;

      const userId = data.user?.id || null;
      setSignedUpUserId(userId);
      setPendingEmail(signupEmail);

      // Move to profile setup step
      setSignupStep('profile');
    } catch (err: any) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const dbRole = selectedRole === 'brand' ? 'seller' : 'reviewer';

      // Upsert user_profiles with role-specific data
      const profileData: any = {
        email: signupEmail,
        full_name: signupName,
        mobile_number: signupMobile || null,
        role: dbRole,
      };

      if (selectedRole === 'reviewer') {
        profileData.bio = reviewerProfile.bio || null;
        profileData.city = reviewerProfile.city || null;
        profileData.categories = reviewerProfile.categories.length > 0 ? reviewerProfile.categories : null;
        profileData.instagram_handle = reviewerProfile.instagram || null;
        profileData.youtube_channel = reviewerProfile.youtube || null;
      }

      if (signedUpUserId) {
        await supabase.from('user_profiles').upsert(
          { id: signedUpUserId, ...profileData },
          { onConflict: 'id' }
        );

        // If brand, also upsert seller_profiles
        if (selectedRole === 'brand') {
          await supabase.from('seller_profiles').upsert({
            user_id: signedUpUserId,
            company_name: brandProfile.company_name || signupName,
            website: brandProfile.website || null,
            industry: brandProfile.industry || null,
            gst_number: brandProfile.gst_number || null,
          }, { onConflict: 'user_id' });
        }
      }

      // Move to email verification step
      setSignupStep('verify-email');
    } catch (err: any) {
      setError(err.message || 'Profile setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || !pendingEmail) return;
    setResendLoading(true);
    try {
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://indianrevi4027.builtwithrocket.new';
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingEmail,
        options: { emailRedirectTo: `${siteUrl}/auth/callback` },
      });
      if (error) throw error;
      setResendCooldown(60);
      setError('__success__Verification email resent! Please check your inbox.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const toggleCategory = (cat: string) => {
    setReviewerProfile((prev) => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter((c) => c !== cat)
        : [...prev.categories, cat],
    }));
  };

  const isSuccess = error.startsWith('__success__');
  const displayMessage = isSuccess ? error.replace('__success__', '') : error;

  const EyeIcon = ({ show }: { show: boolean }) => show ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-border bg-white text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition";

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-foreground items-center justify-center p-12">
        <div className="absolute inset-0 dot-grid-bg opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-0 w-64 h-64 rounded-full pointer-events-none opacity-20" style={{ background: 'radial-gradient(circle, #FF5722 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full pointer-events-none opacity-15" style={{ background: 'radial-gradient(circle, #1A237E 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="relative z-10 max-w-sm text-center">
          <div className="flex justify-center mb-6"><AppLogo size={56} /></div>
          <h2 className="font-heading font-bold text-white text-2xl mb-4 leading-snug">
            India's Most Trusted<br />Review Platform
          </h2>
          <p className="text-white/50 text-sm leading-relaxed mb-8">
            Join 50,000+ verified reviewers and 1,000+ brands growing together.
          </p>
          <div className="flex flex-col gap-3">
            {[
              { icon: 'CurrencyRupeeIcon', text: 'Earn ₹500–₹5,000 per month as a reviewer' },
              { icon: 'ShieldCheckIcon', text: 'AI quality verification' },
              { icon: 'BoltIcon', text: 'UPI payouts within 7 days' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3 text-left bg-white/8 rounded-xl px-4 py-3 border border-white/10">
                <Icon name={item.icon as 'CurrencyRupeeIcon'} size={16} className="text-primary shrink-0" />
                <span className="text-sm text-white/70">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <Link href="/" className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={32} />
          <span className="font-heading font-bold text-base text-foreground">
            IndianReview<span className="text-primary">Club</span>
          </span>
        </Link>

        <div className="w-full max-w-md">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
            <Icon name="ArrowLeftIcon" size={14} className="text-muted-foreground" />
            Back to home
          </Link>

          <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
            {/* Email Verification Screen */}
            {tab === 'signup' && signupStep === 'verify-email' ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <Icon name="EnvelopeIcon" size={28} className="text-primary" />
                </div>
                <h2 className="font-heading font-bold text-xl text-foreground mb-2">Check your email</h2>
                <p className="text-sm text-muted-foreground mb-1">
                  We sent a verification link to
                </p>
                <p className="text-sm font-semibold text-foreground mb-6">{pendingEmail}</p>

                {displayMessage && (
                  <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-medium text-left ${
                    isSuccess ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {displayMessage}
                  </div>
                )}

                <div className="bg-[#F7F8FA] rounded-xl p-4 mb-6 text-left space-y-2">
                  <p className="text-xs font-semibold text-foreground">Next steps:</p>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                    <p className="text-xs text-muted-foreground">Open the email from IndianReviewClub</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                    <p className="text-xs text-muted-foreground">Click the "Confirm your email" button</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                    <p className="text-xs text-muted-foreground">You'll be redirected to your dashboard automatically</p>
                  </div>
                </div>

                <button
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0 || resendLoading}
                  className="w-full border border-border rounded-xl py-2.5 text-sm font-semibold text-foreground hover:bg-[#F7F8FA] transition-colors disabled:opacity-50 mb-3"
                >
                  {resendLoading ? 'Sending…' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend verification email'}
                </button>

                <button
                  onClick={() => { setTab('login'); setSignupStep('credentials'); setError(''); }}
                  className="text-sm text-primary hover:underline font-medium"
                >
                  Back to login
                </button>
              </div>
            ) : tab === 'signup' && signupStep === 'profile' ? (
              /* Profile Setup Step */
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <button onClick={() => setSignupStep('credentials')} className="text-muted-foreground hover:text-foreground transition-colors">
                    <Icon name="ArrowLeftIcon" size={16} />
                  </button>
                  <div>
                    <h1 className="text-lg font-heading font-bold text-foreground">Set up your profile</h1>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {selectedRole === 'reviewer' ? 'Tell brands about yourself' : 'Tell reviewers about your brand'}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full">
                    <Icon name={selectedRole === 'reviewer' ? 'UserIcon' : 'BuildingOfficeIcon'} size={12} />
                    {selectedRole === 'reviewer' ? 'Reviewer' : 'Brand'}
                  </div>
                </div>

                {displayMessage && (
                  <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-medium ${
                    isSuccess ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {displayMessage}
                  </div>
                )}

                <form onSubmit={handleProfileSetup} className="space-y-4">
                  {selectedRole === 'reviewer' ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">City</label>
                        <input
                          type="text"
                          value={reviewerProfile.city}
                          onChange={(e) => setReviewerProfile((p) => ({ ...p, city: e.target.value }))}
                          placeholder="Mumbai, Delhi, Bangalore…"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Bio <span className="text-muted-foreground font-normal">(optional)</span></label>
                        <textarea
                          value={reviewerProfile.bio}
                          onChange={(e) => setReviewerProfile((p) => ({ ...p, bio: e.target.value }))}
                          placeholder="Tell brands a bit about yourself and your reviewing style…"
                          rows={3}
                          className={`${inputCls} resize-none`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Product Categories <span className="text-muted-foreground font-normal">(select all that apply)</span></label>
                        <div className="flex flex-wrap gap-2">
                          {REVIEWER_CATEGORIES.map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => toggleCategory(cat)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                reviewerProfile.categories.includes(cat)
                                  ? 'bg-primary text-white border-primary' :'bg-white text-muted-foreground border-border hover:border-primary hover:text-primary'
                              }`}
                            >
                              {cat}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">Instagram <span className="text-muted-foreground font-normal">(optional)</span></label>
                          <input
                            type="text"
                            value={reviewerProfile.instagram}
                            onChange={(e) => setReviewerProfile((p) => ({ ...p, instagram: e.target.value }))}
                            placeholder="@username"
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">YouTube <span className="text-muted-foreground font-normal">(optional)</span></label>
                          <input
                            type="text"
                            value={reviewerProfile.youtube}
                            onChange={(e) => setReviewerProfile((p) => ({ ...p, youtube: e.target.value }))}
                            placeholder="Channel URL"
                            className={inputCls}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Company / Brand Name</label>
                        <input
                          type="text"
                          required
                          value={brandProfile.company_name}
                          onChange={(e) => setBrandProfile((p) => ({ ...p, company_name: e.target.value }))}
                          placeholder="Acme Pvt. Ltd."
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Website <span className="text-muted-foreground font-normal">(optional)</span></label>
                        <input
                          type="url"
                          value={brandProfile.website}
                          onChange={(e) => setBrandProfile((p) => ({ ...p, website: e.target.value }))}
                          placeholder="https://yourbrand.com"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Industry</label>
                        <select
                          value={brandProfile.industry}
                          onChange={(e) => setBrandProfile((p) => ({ ...p, industry: e.target.value }))}
                          className={inputCls}
                        >
                          <option value="">Select industry…</option>
                          {INDUSTRIES.map((ind) => (
                            <option key={ind} value={ind}>{ind}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">GST Number <span className="text-muted-foreground font-normal">(optional)</span></label>
                        <input
                          type="text"
                          value={brandProfile.gst_number}
                          onChange={(e) => setBrandProfile((p) => ({ ...p, gst_number: e.target.value }))}
                          placeholder="22AAAAA0000A1Z5"
                          className={inputCls}
                        />
                      </div>
                    </>
                  )}

                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setSignupStep('verify-email')}
                      className="flex-1 border border-border rounded-xl py-2.5 text-sm font-semibold text-muted-foreground hover:bg-[#F7F8FA] transition-colors"
                    >
                      Skip for now
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 cta-gradient text-white font-semibold font-heading py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 shadow-sm shadow-primary/20"
                    >
                      {loading ? 'Saving…' : 'Save & Continue'}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div className="flex border-b border-border">
                  <button
                    onClick={() => { setTab('login'); setError(''); setSignupStep('credentials'); }}
                    className={`flex-1 py-4 text-sm font-semibold font-heading transition-colors ${
                      tab === 'login' ? 'text-primary border-b-2 border-primary bg-primary-container/40' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Log In
                  </button>
                  <button
                    onClick={() => { setTab('signup'); setError(''); setSignupStep('credentials'); }}
                    className={`flex-1 py-4 text-sm font-semibold font-heading transition-colors ${
                      tab === 'signup' ? 'text-primary border-b-2 border-primary bg-primary-container/40' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Create Account
                  </button>
                </div>

                <div className="p-6 sm:p-8">
                  <h1 className="text-xl font-heading font-bold text-foreground mb-1">
                    {tab === 'login' ? 'Welcome back' : 'Create your account'}
                  </h1>
                  <p className="text-sm text-muted-foreground mb-6">
                    {tab === 'login' ? 'Log in to access your dashboard.' : 'Join as a reviewer or a brand.'}
                  </p>

                  {displayMessage && (
                    <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-medium ${
                      isSuccess ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {displayMessage}
                    </div>
                  )}

                  {/* Login Form */}
                  {tab === 'login' && (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                        <input type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                        <div className="relative">
                          <input
                            type={showLoginPassword ? 'text' : 'password'}
                            required
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="••••••••"
                            className={`${inputCls} pr-11`}
                          />
                          <button type="button" onClick={() => setShowLoginPassword((v) => !v)} className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle password">
                            <EyeIcon show={showLoginPassword} />
                          </button>
                        </div>
                      </div>
                      <button type="submit" disabled={loading} className="w-full cta-gradient text-white font-semibold font-heading py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 mt-2 shadow-sm shadow-primary/20">
                        {loading ? 'Logging in…' : 'Log In'}
                      </button>
                    </form>
                  )}

                  {/* Signup Form */}
                  {tab === 'signup' && signupStep === 'credentials' && (
                    <form onSubmit={handleSignupCredentials} className="space-y-4">
                      {/* Role selector */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">I am joining as a…</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setSelectedRole('reviewer')}
                            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                              selectedRole === 'reviewer' ?'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                            }`}
                          >
                            <Icon name="UserIcon" size={22} />
                            <div className="text-center">
                              <div className="text-sm font-semibold">Reviewer</div>
                              <div className="text-[10px] mt-0.5 opacity-70">Earn by reviewing products</div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedRole('brand')}
                            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                              selectedRole === 'brand' ?'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                            }`}
                          >
                            <Icon name="BuildingOfficeIcon" size={22} />
                            <div className="text-center">
                              <div className="text-sm font-semibold">Brand / Seller</div>
                              <div className="text-[10px] mt-0.5 opacity-70">Run review campaigns</div>
                            </div>
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Full Name</label>
                        <input type="text" required value={signupName} onChange={(e) => setSignupName(e.target.value)} placeholder="Priya Sharma" className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                        <input type="email" required value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Mobile Number <span className="text-muted-foreground font-normal">(optional)</span></label>
                        <input type="tel" value={signupMobile} onChange={(e) => setSignupMobile(e.target.value)} placeholder="+91 98765 43210" className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                        <div className="relative">
                          <input
                            type={showSignupPassword ? 'text' : 'password'}
                            required
                            value={signupPassword}
                            onChange={(e) => setSignupPassword(e.target.value)}
                            placeholder="Min. 8 characters"
                            className={`${inputCls} pr-11`}
                          />
                          <button type="button" onClick={() => setShowSignupPassword((v) => !v)} className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle password">
                            <EyeIcon show={showSignupPassword} />
                          </button>
                        </div>
                      </div>
                      <button type="submit" disabled={loading} className="w-full cta-gradient text-white font-semibold font-heading py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 mt-2 shadow-sm shadow-primary/20">
                        {loading ? 'Creating account…' : 'Continue →'}
                      </button>
                      <p className="text-xs text-muted-foreground text-center">
                        By signing up, you agree to our{' '}
                        <Link href="/terms" className="text-primary hover:underline">Terms</Link>
                        {' '}and{' '}
                        <Link href="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
                      </p>
                    </form>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
